import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../context/ToastContext';
import Navigation from '../components/Navigation';
import axios from 'axios';
import { Edit, Trash2, ChevronDown, Check, ArrowUp, ArrowDown } from 'lucide-react';
import DashboardTasksList from '../components/DashboardTasksList';
import EditAILimitModal, { EditAILimitTarget } from '../components/EditAILimitModal';
import { NTRP_RATINGS, formatNtrpRating } from '../utils/ntrpRatings';
import { calculateFirstMatchDate, calculateLastMatchDate, Match as LeagueDateMatch } from '../utils/leagueDates';

interface User {
  id: number;
  name: string;
  email: string;
  google_id?: string;
  firebase_uid?: string;
  role: 'ADMIN' | 'PLAYER';
  gender?: 'male' | 'female' | null;
  ntrp_rating?: number | null;
  tennisrecord_name?: string | null;
  created_at: string;
  last_login?: string;
  team_memberships_count?: number;
  subscription?: {
    is_active: boolean;
    max_active_teams: number;
    active_teams_count: number;
    expires_at?: string | null;
  } | null;
}

interface TeamMember {
  id: number;
  team_id: number;
  user_id: number;
  is_captain: boolean;
  join_date: string;
  active_status: boolean;
  user: {
    id: number;
    name: string;
    email: string;
    role: 'admin' | 'player';
  };
}

interface LineupPosition {
  id?: number;
  position_number: number;
  match_type: string;
  lineup_format_id?: number;
}

interface LineupFormat {
  id?: number;
  team_id?: number;
  total_lines: number;
  max_sets_per_line: number;
  positions: LineupPosition[];
  created_at?: string;
}


interface Team {
  id: number;
  name: string;
  league?: string;
  section?: string;
  district_area?: string;
  flight_subflight?: string;
  usta_team_id?: string;
  league_start_date?: string;
  league_end_date?: string;
  use_first_match_for_start_date?: boolean;
  use_last_match_for_end_date?: boolean;
  tennisrecord_url?: string;
  competitive_intel_enabled: boolean;
  created_at: string;
  created_by?: number;
  members: TeamMember[];
  lineup_format?: LineupFormat;
}


interface TeamFormData {
  name: string;
  league: string;
  section: string;
  district_area: string;
  flight_subflight: string;
  usta_team_id: string;
  league_start_date: string;
  league_end_date: string;
  use_first_match_for_start_date: boolean;
  use_last_match_for_end_date: boolean;
  captain_ids: number[];
  total_lines: number;
  max_sets_per_line: number;
  lineup_positions: LineupPosition[];
}

interface UserFormData {
  name: string;
  email: string;
  role: 'ADMIN' | 'PLAYER';
  gender: 'male' | 'female' | '';
  ntrp_rating: number | null;
  tennisrecord_name?: string;
}

interface UserTeamMembership {
  team_id: number;
  team_name: string;
  is_captain: boolean;
  active_status: boolean;
  join_date: string;
}

interface Subscription {
  id: number;
  user_id: number;
  max_active_teams: number;
  is_active: boolean;
  expires_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

interface SubscriptionCapacityInfo {
  max_active_teams: number;
  active_teams_count: number;
  remaining_capacity: number;
  is_active: boolean;
}

interface UserTasksSummary {
  userId: number;
  name: string;
  email: string | null;
  lastLogin: string | null;
  onboardingTasks: Array<{
    task_id: string;
    title: string;
    description: string;
    action_url: string;
    status: string;
  }>;
  dashboardTasks: {
    unsetAvailabilityTasks: Array<{
      teamId: number;
      teamName: string;
      count: number;
      nextMatchDate: string;
    }>;
    incompleteLineupTasks: Array<{
      teamId: number;
      teamName: string;
      count: number;
      nextMatchDate: string;
    }>;
    lineupAvailabilityConflictTasks: Array<{
      matchId: number;
      opponentName: string;
      matchDate: string;
      teamName: string;
    }>;
    captainLineupConflictTasks: Array<{
      matchId: number;
      opponentName: string;
      matchDate: string;
      teamName: string;
      conflictCount: number;
    }>;
    unconfirmedLineupTasks: Array<{
      matchId: number;
      opponentName: string;
      matchDate: string;
      teamName: string;
      unconfirmedCount: number;
    }>;
    pendingInvitationTasks: Array<{
      count: number;
      invitedAt: string;
    }>;
  };
  totalTaskCount: number;
}

const AdminPanelPage: React.FC = () => {
  const toast = useToast();
  const [users, setUsers] = useState<User[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [usersWithTasks, setUsersWithTasks] = useState<UserTasksSummary[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [tasksTeamFilter, setTasksTeamFilter] = useState<number | null>(null);
  // Initialize activeTab from localStorage or default to 'users'
  // If stored tab is 'users-with-tasks', default to 'users' to avoid auto-loading on page load
  const getStoredAdminTab = (): 'users' | 'teams' | 'users-with-tasks' => {
    const stored = localStorage.getItem('admin-activeTab');
    if (stored && ['users', 'teams'].includes(stored)) {
      return stored as 'users' | 'teams';
    }
    // Always default to 'users' on initial load to avoid slow page load
    return 'users';
  };

  const [activeTab, setActiveTab] = useState<'users' | 'teams' | 'users-with-tasks'>(getStoredAdminTab());
  const [tasksTabHasBeenLoaded, setTasksTabHasBeenLoaded] = useState(false);

  // Per-user AI spend snapshot, keyed by user_id. Fetched alongside the users
  // list so each row in the Users tab can show "$X.XX of $Y.YY" inline.
  type AIUsageRow = {
    user_id: number;
    spent_usd: number;
    limit_usd: number;
    percent_used: number;
    is_blocked: boolean;
    has_override: boolean;
  };
  const [aiUsageByUserId, setAIUsageByUserId] = useState<Record<number, AIUsageRow>>({});
  const [aiLimitEditTarget, setAILimitEditTarget] = useState<EditAILimitTarget | null>(null);

  const fetchAIUsage = useCallback(async () => {
    try {
      const response = await axios.get<AIUsageRow[]>('/users/admin/ai-usage');
      const next: Record<number, AIUsageRow> = {};
      for (const row of response.data) next[row.user_id] = row;
      setAIUsageByUserId(next);
    } catch {
      // Soft-fail: the AI column shows "—" if this isn't loaded; the rest of
      // the page is still usable.
    }
  }, []);
  
  // Persist tab changes to localStorage
  useEffect(() => {
    if (activeTab) {
      localStorage.setItem('admin-activeTab', activeTab);
    }
  }, [activeTab]);
  
  // Subscription modal state
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [selectedUserForSubscription, setSelectedUserForSubscription] = useState<User | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<{
    max_active_teams: number;
    is_active: boolean;
    expires_at: string;
    notes: string;
  }>({
    max_active_teams: 3,
    is_active: true,
    expires_at: '',
    notes: ''
  });
  const [subscriptionCapacity, setSubscriptionCapacity] = useState<SubscriptionCapacityInfo | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  
  // Ellipsis menu state
  const [openMenuUserId, setOpenMenuUserId] = useState<number | null>(null);
  const menuRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  
  // NTRP Rating multi-select dropdown state
  const [ntrpDropdownOpen, setNtrpDropdownOpen] = useState(false);
  const ntrpDropdownRef = useRef<HTMLDivElement | null>(null);

  // Handle column sorting
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Modal states
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [teamFormData, setTeamFormData] = useState<TeamFormData>({
    name: '',
    league: '',
    section: '',
    district_area: '',
    flight_subflight: '',
    usta_team_id: '',
    league_start_date: '',
    league_end_date: '',
    use_first_match_for_start_date: true,
    use_last_match_for_end_date: true,
    captain_ids: [],
    total_lines: 5,
    max_sets_per_line: 3,
    lineup_positions: []
  });
  const [teamMatches, setTeamMatches] = useState<LeagueDateMatch[]>([]);
  const [teamFormErrors, setTeamFormErrors] = useState<{ league_start_date?: string; league_end_date?: string }>({});

  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userTeams, setUserTeams] = useState<UserTeamMembership[]>([]);
  const [userFormData, setUserFormData] = useState<UserFormData>({
    name: '',
    email: '',
    role: 'PLAYER',
    gender: '',
    ntrp_rating: null,
    tennisrecord_name: ''
  });


  // Filter state - column-specific filters
  const [userFilters, setUserFilters] = useState({
    nameFilter: '',
    emailFilter: '',
    roleFilter: 'all' as 'all' | 'ADMIN' | 'PLAYER',
    lastLoginFilter: 'all' as 'all' | 'has' | 'never',
    teamsFilter: '',
    genderFilter: 'all' as 'all' | 'male' | 'female' | 'not_set',
    ntrpFilter: [] as (number | 'not_set')[],
    subscriptionFilter: 'all' as 'all' | 'active' | 'inactive' | 'none'
  });

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string | null>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Team filter state - column-specific filters
  const [teamFilters, setTeamFilters] = useState({
    nameFilter: '',
    leagueFilter: '',
    sectionFilter: '',
    activeStatusFilter: 'all' as 'all' | 'active' | 'inactive',
    captainsFilter: [] as number[],
    membersFilter: ''
  });

  // Team sorting state
  const [teamSortColumn, setTeamSortColumn] = useState<string | null>('name');
  const [teamSortDirection, setTeamSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Team captain dropdown state
  const [teamCaptainDropdownOpen, setTeamCaptainDropdownOpen] = useState(false);
  const teamCaptainDropdownRef = useRef<HTMLDivElement | null>(null);

  // Handle team column sorting
  const handleTeamSort = (column: string) => {
    if (teamSortColumn === column) {
      // Toggle direction if same column
      setTeamSortDirection(teamSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to ascending
      setTeamSortColumn(column);
      setTeamSortDirection('asc');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [usersResponse, teamsResponse] = await Promise.all([
        axios.get('/users/?include_subscription=true'),
        axios.get('/teams/')
      ]);
      
      const usersData = usersResponse.data;
      const teamsData = teamsResponse.data;
      
      // Calculate team membership counts for each user
      const userMembershipCounts: { [userId: number]: number } = {};
      teamsData.forEach((team: Team) => {
        team.members.forEach((member: TeamMember) => {
          userMembershipCounts[member.user_id] = (userMembershipCounts[member.user_id] || 0) + 1;
        });
      });
      
      // Merge team membership counts into users
      const usersWithCounts = usersData.map((user: User) => {
        return {
          ...user,
          team_memberships_count: userMembershipCounts[user.id] ?? 0
        };
      });
      
      // Sort users alphabetically by name (case-insensitive)
      const sortedUsers = usersWithCounts.sort((a: User, b: User) => 
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
      
      setUsers(sortedUsers);
      setTeams(teamsData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Load AI spend the first time the Users tab is shown.
  useEffect(() => {
    if (activeTab === 'users') {
      void fetchAIUsage();
    }
  }, [activeTab, fetchAIUsage]);

  // Fetch tasks function
  const fetchTasks = useCallback(async () => {
    try {
      setLoadingTasks(true);
      const params: { days_ahead: number; team_id?: number } = { days_ahead: 90 };
      if (tasksTeamFilter !== null) {
        params.team_id = tasksTeamFilter;
      }
      const response = await axios.get('/users/admin/tasks', { 
        params,
        timeout: 60000 // 60 second timeout for large datasets
      });
      setUsersWithTasks(response.data.users || []);
    } catch (error) {
      console.error('Failed to fetch users with tasks:', error);
      if (axios.isAxiosError(error) && error.code === 'ECONNABORTED') {
        toast.error('Request timed out. Try refreshing the page.');
      } else {
        toast.error('Failed to load users with tasks. Please try again.');
      }
    } finally {
      setLoadingTasks(false);
    }
  }, [tasksTeamFilter, toast]);

  // Fetch tasks when team filter changes (only if tab has been loaded)
  useEffect(() => {
    if (activeTab === 'users-with-tasks' && tasksTabHasBeenLoaded) {
      fetchTasks();
    }
  }, [tasksTeamFilter, activeTab, tasksTabHasBeenLoaded, fetchTasks]);
  
  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openMenuUserId !== null) {
        const menuRef = menuRefs.current[openMenuUserId];
        if (menuRef && !menuRef.contains(event.target as Node)) {
          setOpenMenuUserId(null);
        }
      }
      if (ntrpDropdownOpen && ntrpDropdownRef.current && !ntrpDropdownRef.current.contains(event.target as Node)) {
        setNtrpDropdownOpen(false);
      }
      if (teamCaptainDropdownOpen && teamCaptainDropdownRef.current && !teamCaptainDropdownRef.current.contains(event.target as Node)) {
        setTeamCaptainDropdownOpen(false);
      }
    };

    if (openMenuUserId !== null || ntrpDropdownOpen || teamCaptainDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openMenuUserId, ntrpDropdownOpen, teamCaptainDropdownOpen]);
  


  const openCreateUserModal = () => {
    setEditingUser(null);
    setUserFormData({
      name: '',
      email: '',
      role: 'PLAYER',
      gender: '',
      ntrp_rating: null
    });
    setShowUserModal(true);
  };

  const openEditUserModal = async (user: User) => {
    setEditingUser(user);
    setUserFormData({
      name: user.name,
      email: user.email || '',
      role: user.role,
      gender: user.gender || '',
      ntrp_rating: user.ntrp_rating || null,
      tennisrecord_name: user.tennisrecord_name || ''
    });
    
    // Fetch user's team memberships
    try {
      const response = await axios.get(`/users/${user.id}/teams`);
      setUserTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch user teams:', error);
      setUserTeams([]);
    }
    
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditingUser(null);
    setUserTeams([]);
    setUserFormData({
      name: '',
      email: '',
      role: 'PLAYER',
      gender: '',
      ntrp_rating: null,
      tennisrecord_name: ''
    });
  };

  const openSubscriptionModal = async (user: User) => {
    setSelectedUserForSubscription(user);
    setSubscriptionLoading(true);
    
    try {
      // Fetch existing subscription
      const subscriptionResponse = await axios.get(`/subscriptions/users/${user.id}/subscription`);
      const subscription: Subscription = subscriptionResponse.data;
      
      setSubscriptionData({
        max_active_teams: subscription.max_active_teams,
        is_active: subscription.is_active,
        expires_at: subscription.expires_at ? subscription.expires_at.split('T')[0] : '',
        notes: subscription.notes || ''
      });
      
      // Fetch capacity info
      const capacityResponse = await axios.get(`/subscriptions/users/${user.id}/subscription/capacity`);
      setSubscriptionCapacity(capacityResponse.data);
    } catch (error: any) {
      // If subscription doesn't exist, use defaults
      if (error.response?.status === 404) {
        setSubscriptionData({
          max_active_teams: 3,
          is_active: true,
          expires_at: '',
          notes: ''
        });
        setSubscriptionCapacity(null);
      } else {
        console.error('Failed to fetch subscription:', error);
        toast.error('Failed to load subscription information');
      }
    } finally {
      setSubscriptionLoading(false);
      setShowSubscriptionModal(true);
    }
  };

  const closeSubscriptionModal = () => {
    setShowSubscriptionModal(false);
    setSelectedUserForSubscription(null);
    setSubscriptionData({
      max_active_teams: 3,
      is_active: true,
      expires_at: '',
      notes: ''
    });
    setSubscriptionCapacity(null);
  };

  const saveSubscription = async () => {
    if (!selectedUserForSubscription) return;
    
    try {
      const payload: any = {
        max_active_teams: subscriptionData.max_active_teams,
        is_active: subscriptionData.is_active,
        notes: subscriptionData.notes || null
      };
      
      if (subscriptionData.expires_at) {
        payload.expires_at = `${subscriptionData.expires_at}T00:00:00`;
      } else {
        payload.expires_at = null;
      }
      
      await axios.post(`/subscriptions/users/${selectedUserForSubscription.id}/subscription`, payload);
      toast.success('Subscription updated successfully');
      closeSubscriptionModal();
      fetchData(); // Refresh user list
    } catch (error: any) {
      console.error('Failed to save subscription:', error);
      let errorMessage = 'Failed to save subscription';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleUserFormChange = (field: keyof UserFormData, value: string | number | null) => {
    setUserFormData(prev => ({ ...prev, [field]: value }));
  };

  const saveUser = async () => {
    // Validation
    if (!userFormData.name || !userFormData.name.trim()) {
      toast.warning('Please enter a name');
      return;
    }
    // Email validation - only validate format if email is provided
    if (userFormData.email && userFormData.email.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userFormData.email.trim())) {
        toast.warning('Please enter a valid email address');
        return;
      }
    }

    try {
      if (editingUser) {
        // Update existing user
        const updatePayload: any = {
          name: userFormData.name,
          role: userFormData.role,
          gender: userFormData.gender || null,
          ntrp_rating: userFormData.ntrp_rating || null,
          tennisrecord_name: (userFormData.tennisrecord_name || '').trim() || null
        };
        
        // Only include email if user hasn't authenticated with Google
        if (!editingUser.google_id) {
          const emailValue = (userFormData.email && userFormData.email.trim()) || null;
          if (emailValue !== editingUser.email) {
            updatePayload.email = emailValue;
          }
        }
        
        await axios.patch(`/users/${editingUser.id}`, updatePayload);
        toast.success('User updated successfully');
      } else {
        // Create new user
        const createPayload = {
          ...userFormData,
          email: (userFormData.email && userFormData.email.trim()) || null,
          gender: userFormData.gender || null,
          ntrp_rating: userFormData.ntrp_rating || null,
          tennisrecord_name: (userFormData.tennisrecord_name || '').trim() || null
        };
        await axios.post('/users/', createPayload);
        const emailMessage = createPayload.email 
          ? 'They can now sign in with Google OAuth using this email.'
          : 'They can be added to teams as a guest player.';
        toast.success(`User created successfully. ${emailMessage}`);
      }
      
      // Refresh all data
      await fetchData();
      closeUserModal();
    } catch (error: any) {
      console.error('Failed to save user:', error);
      let errorMessage = 'Failed to save user';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const deleteUser = async (userId: number, userName: string) => {
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/users/${userId}`);
      setUsers(users.filter(u => u.id !== userId));
      toast.success('User deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      let errorMessage = 'Failed to delete user';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const openCreateTeamModal = () => {
    setEditingTeam(null);
    const defaultPositions: LineupPosition[] = Array.from({ length: 5 }, (_, i) => ({
      position_number: i + 1,
      match_type: ''
    }));
    setTeamFormData({
      name: '',
      league: '',
      section: '',
      district_area: '',
      flight_subflight: '',
      usta_team_id: '',
      league_start_date: '',
      league_end_date: '',
      use_first_match_for_start_date: true,
      use_last_match_for_end_date: true,
      captain_ids: [],
      total_lines: 5,
      max_sets_per_line: 3,
      lineup_positions: defaultPositions
    });
    setTeamMatches([]);
    setShowTeamModal(true);
  };

  const openEditTeamModal = (team: Team) => {
    setEditingTeam(team);
    const captains = team.members.filter(m => m.is_captain);
    const captainIds = captains.map(m => m.user_id);
    
    // Set lineup format data or defaults
    const totalLines = team.lineup_format?.total_lines || 5;
    const maxSets = team.lineup_format?.max_sets_per_line || 3;
    const positions = team.lineup_format?.positions || Array.from({ length: totalLines }, (_, i) => ({
      position_number: i + 1,
      match_type: ''
    }));
    
    setTeamFormData({
      name: team.name,
      league: team.league || '',
      section: team.section || '',
      district_area: team.district_area || '',
      flight_subflight: team.flight_subflight || '',
      usta_team_id: team.usta_team_id || '',
      league_start_date: team.league_start_date ? team.league_start_date.split('T')[0] : '',
      league_end_date: team.league_end_date ? team.league_end_date.split('T')[0] : '',
      use_first_match_for_start_date: team.use_first_match_for_start_date ?? false,
      use_last_match_for_end_date: team.use_last_match_for_end_date ?? false,
      captain_ids: captainIds,
      total_lines: totalLines,
      max_sets_per_line: maxSets,
      lineup_positions: positions
    });
    
    // Fetch matches for this team
    axios.get<LeagueDateMatch[]>(`/matches/teams/${team.id}/matches`)
      .then(response => {
        setTeamMatches(response.data);
      })
      .catch(error => {
        console.error('Failed to fetch matches:', error);
        setTeamMatches([]);
      });
    
    setShowTeamModal(true);
  };

  const closeTeamModal = () => {
    setShowTeamModal(false);
    setEditingTeam(null);
    const defaultPositions: LineupPosition[] = Array.from({ length: 5 }, (_, i) => ({
      position_number: i + 1,
      match_type: ''
    }));
    setTeamFormData({
      name: '',
      league: '',
      section: '',
      district_area: '',
      flight_subflight: '',
      usta_team_id: '',
      league_start_date: '',
      league_end_date: '',
      use_first_match_for_start_date: true,
      use_last_match_for_end_date: true,
      captain_ids: [],
      total_lines: 5,
      max_sets_per_line: 3,
      lineup_positions: defaultPositions
    });
    setTeamMatches([]);
    setTeamFormErrors({}); // Clear errors when closing modal
  };

  const handleTeamFormChange = (field: keyof TeamFormData, value: string | number | number[] | boolean) => {
    setTeamFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calculate dates from matches when flags are true
  useEffect(() => {
    if (!showTeamModal || !editingTeam) return;

    if (teamFormData.use_first_match_for_start_date && teamMatches.length > 0) {
      const calculatedDate = calculateFirstMatchDate(teamMatches);
      setTeamFormData(prev => {
        if (prev.league_start_date !== calculatedDate) {
          return { ...prev, league_start_date: calculatedDate };
        }
        return prev;
      });
    }

    if (teamFormData.use_last_match_for_end_date && teamMatches.length > 0) {
      const calculatedDate = calculateLastMatchDate(teamMatches);
      setTeamFormData(prev => {
        if (prev.league_end_date !== calculatedDate) {
          return { ...prev, league_end_date: calculatedDate };
        }
        return prev;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamMatches.length, teamFormData.use_first_match_for_start_date, teamFormData.use_last_match_for_end_date, showTeamModal, editingTeam]);

  const validateTeamDates = (): boolean => {
    const newErrors: { league_start_date?: string; league_end_date?: string } = {};
    
    // Get effective start date (calculated or manual)
    let effectiveStartDate: string | null = null;
    if (teamFormData.use_first_match_for_start_date && teamMatches.length > 0) {
      effectiveStartDate = calculateFirstMatchDate(teamMatches);
    } else if (!teamFormData.use_first_match_for_start_date) {
      effectiveStartDate = teamFormData.league_start_date || null;
    }
    
    // Get effective end date (calculated or manual)
    let effectiveEndDate: string | null = null;
    if (teamFormData.use_last_match_for_end_date && teamMatches.length > 0) {
      effectiveEndDate = calculateLastMatchDate(teamMatches);
    } else if (!teamFormData.use_last_match_for_end_date) {
      effectiveEndDate = teamFormData.league_end_date || null;
    }
    
    // Validate that end date is after start date when both are provided
    if (effectiveStartDate && effectiveEndDate) {
      const startDate = new Date(effectiveStartDate);
      const endDate = new Date(effectiveEndDate);
      if (endDate <= startDate) {
        newErrors.league_end_date = 'End date must be after start date';
        // Also set start date error if it's manually set
        if (!teamFormData.use_first_match_for_start_date) {
          newErrors.league_start_date = 'Start date must be before end date';
        }
      }
    }
    
    setTeamFormErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveTeam = async () => {
    // Validation
    if (!teamFormData.name.trim()) {
      toast.warning('Please enter a team name');
      return;
    }
    
    // Validate dates before saving
    if (!validateTeamDates()) {
      toast.error('Please fix the date validation errors before saving');
      return;
    }
    
    
    // Validate lineup format is provided
    if (!editingTeam && (!teamFormData.lineup_positions || teamFormData.lineup_positions.length === 0)) {
      toast.warning('Lineup format is required at team creation and cannot be changed later');
      return;
    }

    try {
      // Prepare data for API (creator will automatically be made a captain by backend)
      const apiData: any = {
        name: teamFormData.name,
        league: teamFormData.league || null,
        section: teamFormData.section || null,
        district_area: teamFormData.district_area || null,
        flight_subflight: teamFormData.flight_subflight || null,
        usta_team_id: teamFormData.usta_team_id || null,
        league_start_date: teamFormData.league_start_date ? `${teamFormData.league_start_date}T00:00:00` : null,
        league_end_date: teamFormData.league_end_date ? `${teamFormData.league_end_date}T00:00:00` : null,
        use_first_match_for_start_date: teamFormData.use_first_match_for_start_date,
        use_last_match_for_end_date: teamFormData.use_last_match_for_end_date
      };
      
      // Add lineup format if configured
      if (teamFormData.total_lines > 0 && teamFormData.lineup_positions.length > 0) {
        apiData.lineup_format = {
          total_lines: teamFormData.total_lines,
          max_sets_per_line: teamFormData.max_sets_per_line,
          positions: teamFormData.lineup_positions.map(pos => ({
            position_number: pos.position_number,
            match_type: pos.match_type
          }))
        };
      }

      if (editingTeam) {
        // Update existing team
        await axios.put(`/teams/${editingTeam.id}`, apiData);
        toast.success('Team updated successfully');
      } else {
        // Create new team
        await axios.post('/teams/', apiData);
        toast.success('Team created successfully');
      }
      
      // Refresh teams list
      const teamsResponse = await axios.get('/teams/');
      setTeams(teamsResponse.data);
      setTeamFormErrors({}); // Clear errors on success
      closeTeamModal();
    } catch (error: any) {
      console.error('Failed to save team:', error);
      let errorMessage = 'Failed to save team';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Handle validation errors (array of error objects)
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const deleteTeam = async (teamId: number, teamName: string) => {
    if (!window.confirm(`Are you sure you want to delete team "${teamName}"? This will also delete all associated matches and lineups. This action cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`/teams/${teamId}`);
      setTeams(teams.filter(t => t.id !== teamId));
      toast.success('Team deleted successfully');
    } catch (error: any) {
      console.error('Failed to delete team:', error);
      let errorMessage = 'Failed to delete team';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (Array.isArray(detail)) {
          errorMessage = detail.map((err: any) => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  // Filter and search logic
  const filterUsers = useCallback((usersList: User[]) => {
    let filtered = usersList;

    // Apply column-specific filters
    if (userFilters.nameFilter.trim()) {
      const query = userFilters.nameFilter.toLowerCase().trim();
      filtered = filtered.filter((user) => 
        user.name.toLowerCase().includes(query)
      );
    }

    if (userFilters.emailFilter.trim()) {
      const query = userFilters.emailFilter.toLowerCase().trim();
      filtered = filtered.filter((user) => 
        user.email?.toLowerCase().includes(query) ?? false
      );
    }

    if (userFilters.roleFilter !== 'all') {
      filtered = filtered.filter((user) => user.role === userFilters.roleFilter);
    }

    if (userFilters.lastLoginFilter !== 'all') {
      if (userFilters.lastLoginFilter === 'has') {
        filtered = filtered.filter((user) => user.last_login && user.last_login.trim() !== '');
      } else {
        filtered = filtered.filter((user) => !user.last_login || user.last_login.trim() === '');
      }
    }

    if (userFilters.teamsFilter.trim()) {
      const minTeams = parseInt(userFilters.teamsFilter, 10);
      if (!isNaN(minTeams)) {
        filtered = filtered.filter((user) => 
          (user.team_memberships_count ?? 0) >= minTeams
        );
      }
    }

    if (userFilters.genderFilter !== 'all') {
      if (userFilters.genderFilter === 'not_set') {
        filtered = filtered.filter((user) => user.gender === null || user.gender === undefined);
      } else {
        filtered = filtered.filter((user) => user.gender === userFilters.genderFilter);
      }
    }

    if (userFilters.ntrpFilter.length > 0) {
      filtered = filtered.filter((user) => {
        const hasNotSet = userFilters.ntrpFilter.includes('not_set');
        const hasRating = userFilters.ntrpFilter.some((rating) => 
          typeof rating === 'number' && user.ntrp_rating === rating
        );
        if (hasNotSet && hasRating) {
          // Show users with any selected rating OR no rating
          return !user.ntrp_rating || user.ntrp_rating === null || hasRating;
        }
        if (hasNotSet) {
          // Show only users with no rating
          return !user.ntrp_rating || user.ntrp_rating === null;
        }
        // Show only users with selected ratings
        return hasRating;
      });
    }

    if (userFilters.subscriptionFilter !== 'all') {
      if (userFilters.subscriptionFilter === 'none') {
        filtered = filtered.filter((user) => user.subscription === null || user.subscription === undefined);
      } else if (userFilters.subscriptionFilter === 'active') {
        filtered = filtered.filter((user) => user.subscription?.is_active === true);
      } else if (userFilters.subscriptionFilter === 'inactive') {
        filtered = filtered.filter((user) => user.subscription?.is_active === false);
      }
    }

    return filtered;
  }, [userFilters]);

  // Sorting logic
  const sortUsers = useCallback((usersList: User[]) => {
    if (!sortColumn) return usersList;

    const sorted = [...usersList].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'email':
          aValue = a.email?.toLowerCase() || '';
          bValue = b.email?.toLowerCase() || '';
          break;
        case 'role':
          aValue = a.role;
          bValue = b.role;
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login).getTime() : 0;
          bValue = b.last_login ? new Date(b.last_login).getTime() : 0;
          break;
        case 'team_memberships_count':
          aValue = a.team_memberships_count ?? 0;
          bValue = b.team_memberships_count ?? 0;
          break;
        case 'gender':
          aValue = a.gender || '';
          bValue = b.gender || '';
          break;
        case 'ntrp_rating':
          aValue = a.ntrp_rating ?? 0;
          bValue = b.ntrp_rating ?? 0;
          break;
        case 'subscription':
          // Sort by subscription status: active > inactive > none
          const aSubStatus = a.subscription?.is_active === true ? 2 : a.subscription?.is_active === false ? 1 : 0;
          const bSubStatus = b.subscription?.is_active === true ? 2 : b.subscription?.is_active === false ? 1 : 0;
          aValue = aSubStatus;
          bValue = bSubStatus;
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Compare values
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [sortColumn, sortDirection]);

  const filterTeams = useCallback((teamsList: Team[]) => {
    let filtered = teamsList;

    // Apply column-specific filters
    if (teamFilters.nameFilter.trim()) {
      const query = teamFilters.nameFilter.toLowerCase().trim();
      filtered = filtered.filter((team) => 
        team.name.toLowerCase().includes(query)
      );
    }

    if (teamFilters.leagueFilter.trim()) {
      const query = teamFilters.leagueFilter.toLowerCase().trim();
      filtered = filtered.filter((team) => 
        team.league?.toLowerCase().includes(query) ?? false
      );
    }

    if (teamFilters.sectionFilter.trim()) {
      const query = teamFilters.sectionFilter.toLowerCase().trim();
      filtered = filtered.filter((team) => 
        team.section?.toLowerCase().includes(query) ?? false
      );
    }

    // Apply active status filter
    if (teamFilters.activeStatusFilter !== 'all') {
      filtered = filtered.filter((team) => {
        const now = new Date();
        const startDate = team.league_start_date ? new Date(team.league_start_date) : null;
        const endDate = team.league_end_date ? new Date(team.league_end_date) : null;
        
        let isActive = true; // Default to active if no dates
        if (startDate && endDate) {
          isActive = now >= startDate && now <= endDate;
        } else if (startDate) {
          isActive = now >= startDate;
        } else if (endDate) {
          isActive = now <= endDate;
        }
        
        return teamFilters.activeStatusFilter === 'active' ? isActive : !isActive;
      });
    }

    // Apply captain filter
    if (teamFilters.captainsFilter.length > 0) {
      filtered = filtered.filter((team) => {
        const teamCaptainIds = team.members
          .filter(m => m.is_captain)
          .map(m => m.user_id);
        // Team matches if any of the selected captains is a captain of this team
        return teamFilters.captainsFilter.some(captainId => teamCaptainIds.includes(captainId));
      });
    }

    if (teamFilters.membersFilter.trim()) {
      const minMembers = parseInt(teamFilters.membersFilter, 10);
      if (!isNaN(minMembers)) {
        filtered = filtered.filter((team) => 
          team.members.length >= minMembers
        );
      }
    }

    return filtered;
  }, [teamFilters]);

  // Team sorting logic
  const sortTeams = useCallback((teamsList: Team[]) => {
    if (!teamSortColumn) return teamsList;

    const sorted = [...teamsList].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (teamSortColumn) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'league':
          aValue = a.league?.toLowerCase() || '';
          bValue = b.league?.toLowerCase() || '';
          break;
        case 'section':
          aValue = a.section?.toLowerCase() || '';
          bValue = b.section?.toLowerCase() || '';
          break;
        case 'status':
          // Sort by active status: active > inactive
          const aIsActive = (() => {
            const now = new Date();
            const startDate = a.league_start_date ? new Date(a.league_start_date) : null;
            const endDate = a.league_end_date ? new Date(a.league_end_date) : null;
            if (startDate && endDate) {
              return now >= startDate && now <= endDate;
            } else if (startDate) {
              return now >= startDate;
            } else if (endDate) {
              return now <= endDate;
            }
            return true;
          })();
          const bIsActive = (() => {
            const now = new Date();
            const startDate = b.league_start_date ? new Date(b.league_start_date) : null;
            const endDate = b.league_end_date ? new Date(b.league_end_date) : null;
            if (startDate && endDate) {
              return now >= startDate && now <= endDate;
            } else if (startDate) {
              return now >= startDate;
            } else if (endDate) {
              return now <= endDate;
            }
            return true;
          })();
          aValue = aIsActive ? 1 : 0;
          bValue = bIsActive ? 1 : 0;
          break;
        case 'league_start_date':
          aValue = a.league_start_date ? new Date(a.league_start_date).getTime() : 0;
          bValue = b.league_start_date ? new Date(b.league_start_date).getTime() : 0;
          break;
        case 'league_end_date':
          aValue = a.league_end_date ? new Date(a.league_end_date).getTime() : 0;
          bValue = b.league_end_date ? new Date(b.league_end_date).getTime() : 0;
          break;
        case 'members_count':
          aValue = a.members.length;
          bValue = b.members.length;
          break;
        case 'captains_count':
          aValue = a.members.filter(m => m.is_captain).length;
          bValue = b.members.filter(m => m.is_captain).length;
          break;
        default:
          return 0;
      }

      // Handle null/undefined values
      if (aValue === null || aValue === undefined) aValue = '';
      if (bValue === null || bValue === undefined) bValue = '';

      // Compare values
      if (aValue < bValue) return teamSortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return teamSortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [teamSortColumn, teamSortDirection]);

  // Filter and sort users
  const filteredUsers = React.useMemo(() => {
    const filtered = filterUsers(users);
    return sortUsers(filtered);
  }, [users, filterUsers, sortUsers]);

  // Filter and sort teams
  const filteredTeams = React.useMemo(() => {
    const filtered = filterTeams(teams);
    return sortTeams(filtered);
  }, [teams, filterTeams, sortTeams]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navigation />
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 dark:border-blue-400"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Navigation />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Header */}
          <div className="mb-4 md:mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">Admin Panel</h1>
            <p className="text-sm md:text-base text-gray-600 dark:text-gray-400 mt-1">Manage users and teams</p>
          </div>

          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700 mb-4 md:mb-6 overflow-x-auto">
            <nav className="-mb-px flex space-x-4 md:space-x-8 min-w-max">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
                  activeTab === 'users'
                    ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Users ({filteredUsers.length})
              </button>
              <button
                onClick={() => setActiveTab('teams')}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
                  activeTab === 'teams'
                    ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Teams ({filteredTeams.length})
              </button>
              <button
                onClick={async () => {
                  setActiveTab('users-with-tasks');
                  // Fetch data immediately when tab is clicked (only first time)
                  if (!tasksTabHasBeenLoaded) {
                    setTasksTabHasBeenLoaded(true);
                    // Fetch will be triggered by useEffect, but we can also call it directly
                    // to ensure it happens immediately
                    await fetchTasks();
                  }
                }}
                className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap min-h-[44px] ${
                  activeTab === 'users-with-tasks'
                    ? 'border-blue-500 dark:border-blue-400 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 dark:text-gray-300 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                Users with Tasks
              </button>
            </nav>
          </div>

          {/* Users Tab */}
          {activeTab === 'users' && (
            <div>
              <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h2 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">All Users</h2>
                  <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    Manage user accounts, roles, and subscriptions. Pre-create users before their first Google OAuth login.
                  </p>
                </div>
                <button
                  onClick={openCreateUserModal}
                  className="bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-blue-700 min-h-[44px] self-start sm:self-auto"
                >
                  Create User
                </button>
              </div>

              {/* Results Count */}
              {(userFilters.nameFilter || userFilters.emailFilter || userFilters.roleFilter !== 'all' || userFilters.lastLoginFilter !== 'all' || userFilters.teamsFilter || userFilters.genderFilter !== 'all' || userFilters.ntrpFilter.length > 0 || userFilters.subscriptionFilter !== 'all') && (
                <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Showing {filteredUsers.length} of {users.length} users
                </div>
              )}

              {/* Users Table */}
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        {/* Name Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('name')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Name
                              {sortColumn === 'name' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={userFilters.nameFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, nameFilter: e.target.value })}
                              placeholder="Filter name..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Email Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('email')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Email
                              {sortColumn === 'email' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={userFilters.emailFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, emailFilter: e.target.value })}
                              placeholder="Filter email..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Role Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('role')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Role
                              {sortColumn === 'role' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={userFilters.roleFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, roleFilter: e.target.value as 'all' | 'ADMIN' | 'PLAYER' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="ADMIN">Admin</option>
                              <option value="PLAYER">Player</option>
                            </select>
                          </div>
                        </th>

                        {/* Last Login Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('last_login')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Last Login
                              {sortColumn === 'last_login' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={userFilters.lastLoginFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, lastLoginFilter: e.target.value as 'all' | 'has' | 'never' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="has">Has Login</option>
                              <option value="never">Never</option>
                            </select>
                          </div>
                        </th>

                        {/* Teams Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('team_memberships_count')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Teams
                              {sortColumn === 'team_memberships_count' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="number"
                              value={userFilters.teamsFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, teamsFilter: e.target.value })}
                              placeholder="Min teams..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Gender Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('gender')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Gender
                              {sortColumn === 'gender' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={userFilters.genderFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, genderFilter: e.target.value as 'all' | 'male' | 'female' | 'not_set' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="not_set">Not Set</option>
                            </select>
                          </div>
                        </th>

                        {/* NTRP Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('ntrp_rating')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              NTRP
                              {sortColumn === 'ntrp_rating' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <div className="relative" ref={ntrpDropdownRef}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setNtrpDropdownOpen(!ntrpDropdownOpen);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center justify-between"
                              >
                                <span className="truncate">
                                  {userFilters.ntrpFilter.length === 0
                                    ? 'All'
                                    : userFilters.ntrpFilter.length === 1
                                    ? userFilters.ntrpFilter[0] === 'not_set'
                                      ? 'Not Set'
                                      : formatNtrpRating(userFilters.ntrpFilter[0] as number)
                                    : `${userFilters.ntrpFilter.length} selected`}
                                </span>
                                <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${ntrpDropdownOpen ? 'transform rotate-180' : ''}`} />
                              </button>
                              {ntrpDropdownOpen && (
                                <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                  <div className="p-2 space-y-1">
                                    <label className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={userFilters.ntrpFilter.includes('not_set')}
                                        onChange={(e) => {
                                          if (e.target.checked) {
                                            setUserFilters({
                                              ...userFilters,
                                              ntrpFilter: [...userFilters.ntrpFilter, 'not_set']
                                            });
                                          } else {
                                            setUserFilters({
                                              ...userFilters,
                                              ntrpFilter: userFilters.ntrpFilter.filter(r => r !== 'not_set')
                                            });
                                          }
                                        }}
                                        className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
                                      />
                                      <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Not Set</span>
                                      {userFilters.ntrpFilter.includes('not_set') && (
                                        <Check className="ml-auto w-4 h-4 text-blue-600" />
                                      )}
                                    </label>
                                    {NTRP_RATINGS.map((rating) => {
                                      const isSelected = userFilters.ntrpFilter.includes(rating);
                                      return (
                                        <label
                                          key={rating}
                                          className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                setUserFilters({
                                                  ...userFilters,
                                                  ntrpFilter: [...userFilters.ntrpFilter, rating]
                                                });
                                              } else {
                                                setUserFilters({
                                                  ...userFilters,
                                                  ntrpFilter: userFilters.ntrpFilter.filter(r => r !== rating)
                                                });
                                              }
                                            }}
                                            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
                                          />
                                          <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                                            {formatNtrpRating(rating)}
                                          </span>
                                          {isSelected && (
                                            <Check className="ml-auto w-4 h-4 text-blue-600" />
                                          )}
                                        </label>
                                      );
                                    })}
                                  </div>
                                  {userFilters.ntrpFilter.length > 0 && (
                                    <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                                      <button
                                        onClick={() => {
                                          setUserFilters({ ...userFilters, ntrpFilter: [] });
                                          setNtrpDropdownOpen(false);
                                        }}
                                        className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                      >
                                        Clear selection
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </th>

                        {/* Subscription Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleSort('subscription')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Subscription
                              {sortColumn === 'subscription' && (
                                sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={userFilters.subscriptionFilter}
                              onChange={(e) => setUserFilters({ ...userFilters, subscriptionFilter: e.target.value as 'all' | 'active' | 'inactive' | 'none' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                              <option value="none">None</option>
                            </select>
                          </div>
                        </th>

                        {/* AI Spend Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          AI Spend
                        </th>

                        {/* Actions Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{user.name}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {user.email || <span className="text-gray-400 dark:text-gray-500 italic">No email</span>}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'ADMIN' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                              'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                            }`}>
                              {user.role}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {user.team_memberships_count ?? 0}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {user.gender === 'male' ? 'Male' : user.gender === 'female' ? 'Female' : 'Not set'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {user.ntrp_rating ? formatNtrpRating(user.ntrp_rating) : 'Not set'}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {user.subscription ? (
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium ${
                                user.subscription.is_active 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                              }`}>
                                {user.subscription.is_active ? '✓ Active' : 'Inactive'}
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                None
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap" data-testid={`ai-spend-${user.id}`}>
                            {(() => {
                              const usage = aiUsageByUserId[user.id];
                              if (!usage) {
                                return <span className="text-sm text-gray-400 dark:text-gray-500">—</span>;
                              }
                              const badgeClass = usage.is_blocked
                                ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                                : usage.percent_used >= 80
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300';
                              return (
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm text-gray-900 dark:text-gray-100">
                                    ${usage.spent_usd.toFixed(2)} <span className="text-gray-400 dark:text-gray-500">/</span> ${usage.limit_usd.toFixed(2)}
                                    {usage.has_override && (
                                      <span className="ml-1 text-[10px] text-blue-600 dark:text-blue-400">override</span>
                                    )}
                                  </span>
                                  <span className={`inline-flex w-fit items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${badgeClass}`}>
                                    {usage.is_blocked ? 'Blocked' : `${usage.percent_used.toFixed(0)}%`}
                                  </span>
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditUserModal(user)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                title="Edit user"
                              >
                                <Edit className="w-3 h-3" />
                                Edit
                              </button>
                              <button
                                onClick={() => openSubscriptionModal(user)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                title="Manage subscription"
                              >
                                <Edit className="w-3 h-3" />
                                Subscription
                              </button>
                              <button
                                onClick={() => {
                                  const usage = aiUsageByUserId[user.id];
                                  setAILimitEditTarget({
                                    user_id: user.id,
                                    name: user.name,
                                    limit_usd: usage?.limit_usd ?? 0,
                                    has_override: Boolean(usage?.has_override),
                                  });
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                title="Edit monthly AI spend limit"
                              >
                                <Edit className="w-3 h-3" />
                                AI limit
                              </button>
                              <button
                                onClick={() => deleteUser(user.id, user.name)}
                                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="w-3 h-3" />
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Empty States */}
                {filteredUsers.length === 0 && users.length > 0 && (
                  <div className="p-8 md:p-12 text-center">
                    <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No users match your search or filters.</p>
                    <button
                      onClick={() => {
                        setUserFilters({
                          nameFilter: '',
                          emailFilter: '',
                          roleFilter: 'all',
                          lastLoginFilter: 'all',
                          teamsFilter: '',
                          genderFilter: 'all',
                          ntrpFilter: [],
                          subscriptionFilter: 'all'
                        });
                      }}
                      className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium min-h-[44px] px-4"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
                {users.length === 0 && (
                  <div className="p-8 md:p-12 text-center">
                    <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No users found.</p>
                    <button
                      onClick={openCreateUserModal}
                      className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium min-h-[44px] px-4"
                    >
                      Create your first user
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Users with Tasks Tab */}
          {activeTab === 'users-with-tasks' && (
            <div>
              <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h2 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">Users with Outstanding Tasks</h2>
                  <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    View all users who have pending onboarding or dashboard tasks
                  </p>
                </div>
                <button
                  onClick={fetchTasks}
                  disabled={loadingTasks}
                  className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] self-start sm:self-auto"
                >
                  {loadingTasks ? 'Loading...' : 'Refresh'}
                </button>
              </div>

              {/* Team Filter */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Filter by Team
                </label>
                <select
                  value={tasksTeamFilter === null ? '' : tasksTeamFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTasksTeamFilter(value === '' ? null : parseInt(value, 10));
                  }}
                  className="w-full sm:w-auto min-w-[200px] px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                >
                  <option value="">All Teams</option>
                  {teams
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                </select>
              </div>

              {loadingTasks ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center py-4">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400"></div>
                      <span className="text-sm text-gray-600 dark:text-gray-400">Loading users with tasks...</span>
                    </div>
                  </div>
                  {/* Skeleton loaders */}
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 md:p-6 animate-pulse">
                      <div className="flex items-center justify-between mb-3">
                        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded-full w-16"></div>
                      </div>
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-64"></div>
                        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : usersWithTasks.length === 0 ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 text-center">
                  <p className="text-gray-500 dark:text-gray-400">No users with outstanding tasks</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {usersWithTasks.map((userTask) => (
                    <div
                      key={userTask.userId}
                      className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden"
                    >
                      <div
                        className="p-4 md:p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        onClick={() => setExpandedUserId(expandedUserId === userTask.userId ? null : userTask.userId)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3">
                              <h3 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">
                                {userTask.name}
                              </h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                {userTask.totalTaskCount} {userTask.totalTaskCount === 1 ? 'task' : 'tasks'}
                              </span>
                            </div>
                            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 space-y-1">
                              {userTask.email && (
                                <div>Email: {userTask.email}</div>
                              )}
                              <div>
                                Last Login: {userTask.lastLogin ? new Date(userTask.lastLogin).toLocaleString() : 'Never'}
                              </div>
                            </div>
                          </div>
                          <ChevronDown
                            className={`w-5 h-5 text-gray-400 transition-transform ${
                              expandedUserId === userTask.userId ? 'transform rotate-180' : ''
                            }`}
                          />
                        </div>
                      </div>

                      {expandedUserId === userTask.userId && (
                        <div className="border-t border-gray-200 dark:border-gray-700 p-4 md:p-6">
                          <DashboardTasksList
                            dashboardTasks={userTask.dashboardTasks}
                            onboardingTasks={userTask.onboardingTasks}
                            teams={teams}
                            emptyMessage="This user doesn't have any tasks at the moment."
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Teams Tab */}
          {activeTab === 'teams' && (
            <div>
              <div className="mb-4 md:mb-6 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
                <div>
                  <h2 className="text-base md:text-lg font-medium text-gray-900 dark:text-gray-100">All Teams</h2>
                  <p className="mt-1 text-xs md:text-sm text-gray-500 dark:text-gray-400">
                    View and manage all teams
                  </p>
                </div>
                <button
                  onClick={openCreateTeamModal}
                  className="bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-blue-700 min-h-[44px] self-start sm:self-auto"
                >
                  Create Team
                </button>
              </div>

              {/* Results Count */}
              {(teamFilters.nameFilter || teamFilters.leagueFilter || teamFilters.sectionFilter || teamFilters.activeStatusFilter !== 'all' || teamFilters.captainsFilter.length > 0 || teamFilters.membersFilter) && (
                <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                  Showing {filteredTeams.length} of {teams.length} teams
                </div>
              )}

              {/* Teams Table */}
              <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        {/* Name Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('name')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Name
                              {teamSortColumn === 'name' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={teamFilters.nameFilter}
                              onChange={(e) => setTeamFilters({ ...teamFilters, nameFilter: e.target.value })}
                              placeholder="Filter name..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* League Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('league')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              League
                              {teamSortColumn === 'league' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={teamFilters.leagueFilter}
                              onChange={(e) => setTeamFilters({ ...teamFilters, leagueFilter: e.target.value })}
                              placeholder="Filter league..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Section Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('section')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Section
                              {teamSortColumn === 'section' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="text"
                              value={teamFilters.sectionFilter}
                              onChange={(e) => setTeamFilters({ ...teamFilters, sectionFilter: e.target.value })}
                              placeholder="Filter section..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Status Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('status')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Status
                              {teamSortColumn === 'status' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <select
                              value={teamFilters.activeStatusFilter}
                              onChange={(e) => setTeamFilters({ ...teamFilters, activeStatusFilter: e.target.value as 'all' | 'active' | 'inactive' })}
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <option value="all">All</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                        </th>

                        {/* Start Date Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('league_start_date')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Start Date
                              {teamSortColumn === 'league_start_date' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <div className="h-6"></div>
                          </div>
                        </th>

                        {/* End Date Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('league_end_date')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              End Date
                              {teamSortColumn === 'league_end_date' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <div className="h-6"></div>
                          </div>
                        </th>

                        {/* Members Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('members_count')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Members
                              {teamSortColumn === 'members_count' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <input
                              type="number"
                              value={teamFilters.membersFilter}
                              onChange={(e) => setTeamFilters({ ...teamFilters, membersFilter: e.target.value })}
                              placeholder="Min members..."
                              className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                        </th>

                        {/* Captains Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          <div className="flex flex-col gap-2">
                            <button
                              onClick={() => handleTeamSort('captains_count')}
                              className="flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200"
                            >
                              Captains
                              {teamSortColumn === 'captains_count' && (
                                teamSortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              )}
                            </button>
                            <div className="relative" ref={teamCaptainDropdownRef}>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setTeamCaptainDropdownOpen(!teamCaptainDropdownOpen);
                                }}
                                className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center justify-between"
                              >
                                <span className="truncate">
                                  {teamFilters.captainsFilter.length === 0
                                    ? 'All'
                                    : teamFilters.captainsFilter.length === 1
                                    ? (() => {
                                        const captainId = teamFilters.captainsFilter[0];
                                        const captain = users.find(u => u.id === captainId);
                                        return captain ? captain.name : 'Unknown';
                                      })()
                                    : `${teamFilters.captainsFilter.length} selected`}
                                </span>
                                <ChevronDown className={`w-3 h-3 transition-transform flex-shrink-0 ${teamCaptainDropdownOpen ? 'transform rotate-180' : ''}`} />
                              </button>
                              {teamCaptainDropdownOpen && (() => {
                                // Get all user IDs who are captains of at least one team
                                const captainUserIds = new Set<number>();
                                teams.forEach(team => {
                                  team.members.forEach(member => {
                                    if (member.is_captain) {
                                      captainUserIds.add(member.user_id);
                                    }
                                  });
                                });
                                
                                // Filter users to only show those who are captains
                                const captainUsers = users.filter(user => captainUserIds.has(user.id));
                                
                                return (
                                  <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto">
                                    <div className="p-2 space-y-1">
                                      {captainUsers.length > 0 ? (
                                        captainUsers.map((user) => {
                                          const isSelected = teamFilters.captainsFilter.includes(user.id);
                                          return (
                                            <label
                                              key={user.id}
                                              className="flex items-center px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                                            >
                                              <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) => {
                                                  if (e.target.checked) {
                                                    setTeamFilters({
                                                      ...teamFilters,
                                                      captainsFilter: [...teamFilters.captainsFilter, user.id]
                                                    });
                                                  } else {
                                                    setTeamFilters({
                                                      ...teamFilters,
                                                      captainsFilter: teamFilters.captainsFilter.filter(id => id !== user.id)
                                                    });
                                                  }
                                                }}
                                                className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500 bg-white dark:bg-gray-700"
                                              />
                                              <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                                                {user.name}
                                              </span>
                                              {isSelected && (
                                                <Check className="ml-auto w-4 h-4 text-blue-600" />
                                              )}
                                            </label>
                                          );
                                        })
                                      ) : (
                                        <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                                          No captains found
                                        </div>
                                      )}
                                    </div>
                                    {teamFilters.captainsFilter.length > 0 && (
                                      <div className="border-t border-gray-200 dark:border-gray-700 p-2">
                                        <button
                                          onClick={() => {
                                            setTeamFilters({ ...teamFilters, captainsFilter: [] });
                                            setTeamCaptainDropdownOpen(false);
                                          }}
                                          className="w-full text-left px-3 py-2 text-sm text-blue-600 dark:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                                        >
                                          Clear selection
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          </div>
                        </th>

                        {/* Actions Column */}
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                      {filteredTeams.map((team) => {
                        // Calculate if team is active based on league dates
                        const now = new Date();
                        const startDate = team.league_start_date ? new Date(team.league_start_date) : null;
                        const endDate = team.league_end_date ? new Date(team.league_end_date) : null;
                        
                        let isActive = true; // Default to active if no dates
                        if (startDate && endDate) {
                          isActive = now >= startDate && now <= endDate;
                        } else if (startDate) {
                          isActive = now >= startDate;
                        } else if (endDate) {
                          isActive = now <= endDate;
                        }
                        
                        const captains = team.members.filter(m => m.is_captain);
                        
                        return (
                          <tr key={team.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{team.name}</span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {team.league || <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {team.section || <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                isActive 
                                  ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                  : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                              }`}>
                                {isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {team.league_start_date ? new Date(team.league_start_date).toLocaleDateString() : <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {team.league_end_date ? new Date(team.league_end_date).toLocaleDateString() : <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {team.members.length}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className="text-sm text-gray-900 dark:text-gray-100">
                                {captains.length > 0 ? (
                                  captains.map((member, idx, arr) => (
                                    <span key={member.id}>
                                      {member.user.name}
                                      {idx < arr.length - 1 && ', '}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-gray-400 dark:text-gray-500 italic">Not set</span>
                                )}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => openEditTeamModal(team)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                                  title="Edit team"
                                >
                                  <Edit className="w-3 h-3" />
                                  Edit
                                </button>
                                <button
                                  onClick={() => deleteTeam(team.id, team.name)}
                                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                  title="Delete team"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Empty States */}
                {filteredTeams.length === 0 && teams.length > 0 && (
                  <div className="p-8 md:p-12 text-center">
                    <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No teams match your filters.</p>
                    <button
                      onClick={() => {
                        setTeamFilters({
                          nameFilter: '',
                          leagueFilter: '',
                          sectionFilter: '',
                          activeStatusFilter: 'all',
                          captainsFilter: [],
                          membersFilter: ''
                        });
                      }}
                      className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium min-h-[44px] px-4"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
                {teams.length === 0 && (
                  <div className="p-8 md:p-12 text-center">
                    <p className="text-sm md:text-base text-gray-500 dark:text-gray-400">No teams created yet.</p>
                    <button
                      onClick={openCreateTeamModal}
                      className="mt-4 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 text-sm font-medium min-h-[44px] px-4"
                    >
                      Create your first team
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && selectedUserForSubscription && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-opacity-70 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 md:top-20 mx-auto p-4 md:p-5 border border-gray-200 dark:border-gray-700 w-full max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">
                Manage Subscription - {selectedUserForSubscription.name}
              </h3>
              
              {subscriptionLoading ? (
                <div className="text-center py-8">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading subscription...</p>
                </div>
              ) : (
                <>
                  {/* Current Capacity Info */}
                  {subscriptionCapacity && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md">
                      <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-300 mb-2">Current Status</h4>
                      <div className="space-y-1 text-sm">
                        <p className="text-blue-800 dark:text-blue-200">
                          <span className="font-medium">Active Teams:</span> {subscriptionCapacity.active_teams_count} / {subscriptionCapacity.max_active_teams}
                        </p>
                        <p className="text-blue-800 dark:text-blue-200">
                          <span className="font-medium">Remaining Capacity:</span> {subscriptionCapacity.remaining_capacity}
                        </p>
                        <p className="text-blue-800 dark:text-blue-200">
                          <span className="font-medium">Status:</span> {subscriptionCapacity.is_active ? 'Active' : 'Inactive'}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Active Teams *
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={subscriptionData.max_active_teams}
                        onChange={(e) => setSubscriptionData({
                          ...subscriptionData,
                          max_active_teams: parseInt(e.target.value) || 0
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Maximum number of active teams this user can be captain of simultaneously
                      </p>
                    </div>

                    <div>
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={subscriptionData.is_active}
                          onChange={(e) => setSubscriptionData({
                            ...subscriptionData,
                            is_active: e.target.checked
                          })}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Subscription is Active
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 ml-6">
                        Inactive subscriptions prevent users from creating new teams
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Expiration Date (Optional)
                      </label>
                      <input
                        type="date"
                        value={subscriptionData.expires_at}
                        onChange={(e) => setSubscriptionData({
                          ...subscriptionData,
                          expires_at: e.target.value
                        })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Leave empty for no expiration
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Notes (Optional)
                      </label>
                      <textarea
                        value={subscriptionData.notes}
                        onChange={(e) => setSubscriptionData({
                          ...subscriptionData,
                          notes: e.target.value
                        })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[80px]"
                        placeholder="e.g., Premium plan, Promotional subscription, etc."
                      />
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-3 mt-6">
                    <button
                      onClick={saveSubscription}
                      className="flex-1 bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-blue-700 min-h-[44px]"
                    >
                      Save Subscription
                    </button>
                    <button
                      onClick={closeSubscriptionModal}
                      className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px]"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Modal */}
      {showUserModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-opacity-70 overflow-y-auto h-full w-full z-50 p-4">
          <div className="relative top-4 md:top-20 mx-auto p-4 md:p-5 border border-gray-200 dark:border-gray-700 w-full max-w-md shadow-lg rounded-md bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">
                {editingUser ? 'Edit User' : 'Create New User'}
              </h3>
              
              {!editingUser && (
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                  Pre-create a user account. When they first sign in with Google OAuth using this email, their account will be linked automatically.
                </p>
              )}
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={userFormData.name}
                    onChange={(e) => handleUserFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    placeholder="e.g., John Smith"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    TennisRecord.com name (optional)
                  </label>
                  <input
                    type="text"
                    value={userFormData.tennisrecord_name || ''}
                    onChange={(e) => handleUserFormChange('tennisrecord_name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    placeholder="Exact name from TennisRecord.com"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Use the exact name as it appears on tennisrecord.com.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email Address
                    {editingUser?.google_id && (
                      <span className="ml-2 text-xs font-normal text-green-600 dark:text-green-400">
                        ✓ Authenticated
                      </span>
                    )}
                  </label>
                  <input
                    type="email"
                    value={userFormData.email}
                    onChange={(e) => handleUserFormChange('email', e.target.value)}
                    disabled={!!(editingUser?.google_id)}
                    className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] ${
                      editingUser?.google_id 
                        ? 'bg-gray-100 dark:bg-gray-700/50 cursor-not-allowed text-gray-500 dark:text-gray-400' 
                        : 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                    }`}
                    placeholder="e.g., john@example.com"
                  />
                  {editingUser?.google_id ? (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Email cannot be changed after Google authentication
                    </p>
                  ) : editingUser && !editingUser.google_id ? (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Email can be changed (user hasn't signed in with Google yet)
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Optional - Must match their Google account email if provided
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Role *
                  </label>
                  <select
                    value={userFormData.role}
                    onChange={(e) => handleUserFormChange('role', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                  >
                    <option value="PLAYER">Player</option>
                    <option value="ADMIN">Admin</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Team captain status is set per-team in the Teams section
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Gender
                  </label>
                  <select
                    value={userFormData.gender}
                    onChange={(e) => handleUserFormChange('gender', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                  >
                    <option value="">Not Set</option>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Used for lineup validation (e.g., mixed doubles requires one male and one female)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    NTRP Rating
                  </label>
                  <select
                    value={userFormData.ntrp_rating || ''}
                    onChange={(e) => handleUserFormChange('ntrp_rating', e.target.value === '' ? null : parseFloat(e.target.value))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                  >
                    <option value="">Not Set</option>
                    {NTRP_RATINGS.map((rating) => (
                      <option key={rating} value={rating}>
                        {formatNtrpRating(rating)}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    National Tennis Rating Program rating level
                  </p>
                </div>
              </div>

              {/* Team Memberships (shown when editing user) */}
              {editingUser && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Team Memberships
                  </label>
                  {userTeams.length > 0 ? (
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-md p-3 max-h-48 overflow-y-auto">
                      <div className="space-y-2">
                        {userTeams.map((membership) => (
                          <div 
                            key={membership.team_id} 
                            className="flex items-center justify-between bg-white dark:bg-gray-800 p-2 rounded border border-gray-200 dark:border-gray-700"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {membership.team_name}
                              </p>
                            </div>
                            <div className="flex items-center space-x-2">
                              {membership.is_captain && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                  Captain
                                </span>
                              )}
                              {!membership.active_status && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300">
                                  Inactive
                                </span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 rounded-md p-3">
                      Not a member of any teams
                    </p>
                  )}
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={saveUser}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-blue-700 min-h-[44px]"
                >
                  {editingUser ? 'Update User' : 'Create User'}
                </button>
                <button
                  onClick={closeUserModal}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 dark:bg-opacity-70 overflow-y-auto h-full w-full z-50 p-0 md:p-4">
          <div className="relative top-0 md:top-10 mx-auto p-4 md:p-5 border-0 md:border border-gray-200 dark:border-gray-700 w-full max-w-2xl shadow-lg rounded-none md:rounded-md bg-white dark:bg-gray-800 max-h-[100vh] md:max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100 mb-4">
                {editingTeam ? 'Edit Team' : 'Create New Team'}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Team Name *
                  </label>
                  <input
                    type="text"
                    value={teamFormData.name}
                    onChange={(e) => handleTeamFormChange('name', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    placeholder="e.g., Springfield Tennis Team"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    League
                  </label>
                  <input
                    type="text"
                    value={teamFormData.league}
                    onChange={(e) => handleTeamFormChange('league', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                    placeholder="e.g., USTA League, Mixed Doubles"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      USTA Section
                    </label>
                    <input
                      type="text"
                      value={teamFormData.section}
                      onChange={(e) => handleTeamFormChange('section', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      placeholder="e.g., Southern, Mid-Atlantic"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      District/Area
                    </label>
                    <input
                      type="text"
                      value={teamFormData.district_area}
                      onChange={(e) => handleTeamFormChange('district_area', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      placeholder="e.g., District 5"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Flight/Sub-Flight
                    </label>
                    <input
                      type="text"
                      value={teamFormData.flight_subflight}
                      onChange={(e) => handleTeamFormChange('flight_subflight', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      placeholder="e.g., Flight 1"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      USTA Team ID
                    </label>
                    <input
                      type="text"
                      value={teamFormData.usta_team_id}
                      onChange={(e) => handleTeamFormChange('usta_team_id', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      placeholder="e.g., 12-345678"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        type="checkbox"
                        id="admin_use_first_match"
                        checked={teamFormData.use_first_match_for_start_date}
                        onChange={(e) => {
                          const useCalculated = e.target.checked;
                          handleTeamFormChange('use_first_match_for_start_date', useCalculated);
                          // Clear manual date when using calculated date
                          if (useCalculated) {
                            handleTeamFormChange('league_start_date', '');
                            // Clear errors when toggling
                            if (teamFormErrors.league_start_date) {
                              setTeamFormErrors({ ...teamFormErrors, league_start_date: undefined });
                            }
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="admin_use_first_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Use first match date
                      </label>
                    </div>
                    {!teamFormData.use_first_match_for_start_date && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          League Start Date
                        </label>
                        <input
                          type="date"
                          value={teamFormData.league_start_date}
                          onChange={(e) => {
                            handleTeamFormChange('league_start_date', e.target.value);
                            // Clear error when user changes the date
                            if (teamFormErrors.league_start_date) {
                              setTeamFormErrors({ ...teamFormErrors, league_start_date: undefined });
                            }
                          }}
                          className={`w-full px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] ${
                            teamFormErrors.league_start_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {teamFormErrors.league_start_date && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{teamFormErrors.league_start_date}</p>
                        )}
                      </>
                    )}
                    {teamFormData.use_first_match_for_start_date && teamMatches.length === 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Date will be calculated when matches are added
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <input
                        type="checkbox"
                        id="admin_use_last_match"
                        checked={teamFormData.use_last_match_for_end_date}
                        onChange={(e) => {
                          const useCalculated = e.target.checked;
                          handleTeamFormChange('use_last_match_for_end_date', useCalculated);
                          // Clear manual date when using calculated date
                          if (useCalculated) {
                            handleTeamFormChange('league_end_date', '');
                            // Clear errors when toggling
                            if (teamFormErrors.league_end_date) {
                              setTeamFormErrors({ ...teamFormErrors, league_end_date: undefined });
                            }
                          }
                        }}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="admin_use_last_match" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Use last match date
                      </label>
                    </div>
                    {!teamFormData.use_last_match_for_end_date && (
                      <>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          League End Date
                        </label>
                        <input
                          type="date"
                          value={teamFormData.league_end_date}
                          onChange={(e) => {
                            handleTeamFormChange('league_end_date', e.target.value);
                            // Clear error when user changes the date
                            if (teamFormErrors.league_end_date) {
                              setTeamFormErrors({ ...teamFormErrors, league_end_date: undefined });
                            }
                          }}
                          className={`w-full px-3 py-2 border bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px] ${
                            teamFormErrors.league_end_date ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                          }`}
                        />
                        {teamFormErrors.league_end_date && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">{teamFormErrors.league_end_date}</p>
                        )}
                      </>
                    )}
                    {teamFormData.use_last_match_for_end_date && teamMatches.length === 0 && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Date will be calculated when matches are added
                      </p>
                    )}
                  </div>
                </div>

                {/* Lineup Format Configuration */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <h4 className="text-md font-medium text-gray-900 dark:text-gray-100 mb-3">Lineup Format Configuration</h4>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Total Number of Lines / Positions *
                      </label>
                      <select
                        value={teamFormData.total_lines}
                        onChange={(e) => {
                          const newTotal = parseInt(e.target.value) || 1;
                          setTeamFormData(prev => {
                            // Adjust positions array to match new total
                            const currentPositions = [...prev.lineup_positions];
                            const newPositions: LineupPosition[] = [];
                            
                            for (let i = 0; i < newTotal; i++) {
                              if (i < currentPositions.length) {
                                newPositions.push(currentPositions[i]);
                              } else {
                                newPositions.push({
                                  position_number: i + 1,
                                  match_type: ''
                                });
                              }
                            }
                            
                            return {
                              ...prev,
                              total_lines: newTotal,
                              lineup_positions: newPositions
                            };
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Sets Played Per Line *
                      </label>
                      <select
                        value={teamFormData.max_sets_per_line}
                        onChange={(e) => handleTeamFormChange('max_sets_per_line', parseInt(e.target.value) || 3)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                      >
                        {[1, 3, 5].map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Match Type per Line
                    </label>
                    <div className="space-y-2">
                      {teamFormData.lineup_positions.map((position, index) => (
                        <div key={index} className="flex items-center space-x-3">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 w-16">Line {position.position_number}</span>
                          <select
                            value={position.match_type}
                            onChange={(e) => {
                              const newPositions = [...teamFormData.lineup_positions];
                              newPositions[index] = {
                                ...newPositions[index],
                                match_type: e.target.value
                              };
                              setTeamFormData(prev => ({
                                ...prev,
                                lineup_positions: newPositions
                              }));
                            }}
                            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[44px]"
                          >
                            <option value="">Please Select</option>
                            <option value="mens_doubles">Mens Doubles Match</option>
                            <option value="womens_doubles">Womens Doubles Match</option>
                            <option value="mixed_doubles">Mixed Doubles Match</option>
                            <option value="mens_singles">Mens Singles Match</option>
                            <option value="womens_singles">Womens Singles Match</option>
                          </select>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  onClick={saveTeam}
                  className="flex-1 bg-blue-600 text-white px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-blue-700 min-h-[44px]"
                >
                  {editingTeam ? 'Update Team' : 'Create Team'}
                </button>
                <button
                  onClick={closeTeamModal}
                  className="flex-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 px-4 py-3 md:py-2 rounded-md text-sm font-medium hover:bg-gray-300 dark:hover:bg-gray-600 min-h-[44px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {aiLimitEditTarget && (
        <EditAILimitModal
          target={aiLimitEditTarget}
          onClose={() => setAILimitEditTarget(null)}
          onSaved={(updated) => {
            // Patch the in-memory map so the row reflects the new limit without a full reload.
            setAIUsageByUserId((prev) => {
              const existing = prev[updated.user_id];
              return {
                ...prev,
                [updated.user_id]: {
                  ...existing,
                  user_id: updated.user_id,
                  limit_usd: updated.limit_usd,
                  has_override: updated.has_override,
                  spent_usd: existing?.spent_usd ?? 0,
                  percent_used: existing && updated.limit_usd > 0
                    ? (existing.spent_usd / updated.limit_usd) * 100
                    : 0,
                  is_blocked: existing
                    ? existing.spent_usd >= updated.limit_usd
                    : false,
                },
              };
            });
            setAILimitEditTarget(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminPanelPage;
